
import React, { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  phone: z.string().min(5, { message: "Phone number must be at least 5 characters" }),
});

type FormData = z.infer<typeof formSchema>;

interface InviteSuperAdminFormProps {
  onInvite: (data: FormData) => Promise<boolean>;
  remainingSlots: number;
}

const InviteSuperAdminForm: React.FC<InviteSuperAdminFormProps> = ({ onInvite, remainingSlots }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
    },
  });

  const handleSubmit = async (data: FormData) => {
    console.log('InviteSuperAdminForm - Handling submit with data:', data);
    
    if (remainingSlots <= 0) {
      toast({
        title: "No available slots",
        description: "You've reached the maximum number of admin managers for your plan.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log('InviteSuperAdminForm - Calling onInvite function');
      const success = await onInvite(data);
      console.log('InviteSuperAdminForm - Invite result:', success);
      
      if (success) {
        toast({
          title: "Invitation sent",
          description: `An invitation has been sent to ${data.email}`,
        });
        form.reset();
      } else {
        toast({
          title: "Error",
          description: "Failed to send invitation. Please try again later.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error inviting super admin:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invite Super Admin Manager</CardTitle>
        <CardDescription>
          {remainingSlots > 0 
            ? `You can invite ${remainingSlots} more managers to your team.`
            : "You've reached your plan's limit for admin managers."
          }
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input placeholder="john@example.com" type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input placeholder="+1 234 567 8910" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <Button 
              type="submit" 
              disabled={isSubmitting || remainingSlots <= 0} 
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending Invitation...
                </>
              ) : (
                'Send Invitation'
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default InviteSuperAdminForm;
